//!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!! VERSION PREMIUM DESIGN
//
// NOTA DE IMPLEMENTACIÓN (carpetas):
// - Se agregó un nivel de "carpetas" sobre la vista de documentos: Partes Diarios,
//   Seguros Personales, Seguros Vehiculares, Seguros de Equipos y Cláusula de No
//   Repetición del proyecto.
// - Cada documento se clasifica con un campo `categoria` (doc.categoria o
//   doc.campos.categoria). Los documentos existentes que no tengan ese campo se
//   asumen como "partes_diarios" para no romper compatibilidad con lo ya cargado.
// - Al subir un archivo se envía `categoria` al backend junto con `pozo`, igual que
//   ya se hacía con `pozo`. Hay que confirmar con el backend el nombre exacto del
//   campo y persistirlo en /api/upload, /api/ocr-results-db y /api/download-all-pdfs.
// - Si la ruta trae `wellId` (se entra desde un pozo puntual), se abre directo la
//   carpeta "Partes Diarios" para no cambiar el flujo actual. Si no hay wellId, se
//   muestra primero la grilla de carpetas.
//
import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import {
  Box, Typography, Grid, Card, CardContent, IconButton, Tooltip,
  Button, Divider, CircularProgress, Collapse, TextField, Snackbar,
  Alert, ButtonGroup, Skeleton, Paper, Menu, MenuItem, ListItemIcon,
  ListItemText, Dialog, DialogContent, FormControl, InputLabel, Select,
  Drawer
} from "@mui/material";
import {
  CloudDownload, UploadFile, Description, Visibility, VisibilityOff,
  Edit, Save, ZoomIn, ZoomOut, MailOutline, FolderZip, TableChart,
  AutoAwesome, KeyboardArrowDown, Close, Folder, HealthAndSafety,
  DirectionsCar, Engineering, Gavel, ArrowBack, ChevronRight, HelpOutline
} from "@mui/icons-material";
import { useAuth } from "../context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { Document, Page, pdfjs } from "react-pdf";
import { useParams, useNavigate } from "react-router-dom";
import DocumentosOnboardingTour, { useDocumentosTour } from "./DocumentosOnboardingTour";
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;
dayjs.extend(require("dayjs/plugin/customParseFormat"));
dayjs.extend(require("dayjs/plugin/isSameOrAfter"));

const DESIGN = {
  navy: "#0a0f2e",
  navyMid: "#111844",
  blue: "#1565C0",
  blueBright: "#2979FF",
  accent: "#00E5FF",
  accentSoft: "rgba(0,229,255,0.12)",
  surface: "#ffffff",
  surfaceAlt: "#f4f6fb",
  border: "rgba(21,101,192,0.13)",
  borderHover: "rgba(41,121,255,0.35)",
  text: "#0d1b3e",
  textMid: "#3a4a6b",
  textSoft: "#7a8fb5",
  shadow: "0 4px 24px rgba(10,15,46,0.10)",
  shadowHover: "0 12px 40px rgba(21,101,192,0.18)",
  radius: "16px",
  radiusSm: "10px",
};

const CAMPOS_ESPERADOS = [
  "fecha", "turno", "proyecto", "pozo", "ecoperf_max_l", "ecoperf_max_s", "eco_ph",
  "ecovis", "ecotrol", "cemento", "cal", "prof_inicial", "prof_final",
  "metros_perf", "hs_perf", "gasoil", "nafta", "agua", "bentonita",
  "ecozan", "ecodisper_max", "ecolubr", "soda_caustica", "supervisor", "perforista", "geologo", "gasoil_perforadora_lts", "gasoil_generador_lts", "gasoil_total_lts", "agua_viajes", "desarrollo_trabajo", "observaciones_tecnicas", "observaciones_geologicas",
];
const ETIQUETAS_CAMPOS = Object.fromEntries(CAMPOS_ESPERADOS.map((k) => [k, `document.${k}`]));
const PLACEHOLDERS = Object.fromEntries(CAMPOS_ESPERADOS.map((k) => [k, `document.placeholder.${k}`]));

// ----------------------------------------------------------------------------------
// CARPETAS
// ----------------------------------------------------------------------------------
const FOLDERS = [
  {
    key: "partes_diarios",
    icon: Description,
    color: DESIGN.blue,
    usesPozo: true,
  },
  {
    key: "seguros_personales",
    icon: HealthAndSafety,
    color: "#8b5cf6",
    usesPozo: false,
  },
  {
    key: "seguros_vehiculares",
    icon: DirectionsCar,
    color: "#f59e0b",
    usesPozo: false,
  },
  {
    key: "seguros_equipos",
    icon: Engineering,
    color: "#22c55e",
    usesPozo: false,
  },
  {
    key: "clausula_no_repeticion",
    icon: Gavel,
    color: "#ef4444",
    usesPozo: false,
  },
];

const getCategoria = (doc) => doc?.categoria || doc?.campos?.categoria || "partes_diarios";
const getFolderMeta = (key) => FOLDERS.find((f) => f.key === key) || FOLDERS[0];

const DocumentSkeleton = () => (
  <Card elevation={0} sx={{ borderRadius: DESIGN.radius, border: `1px solid ${DESIGN.border}`, overflow: "hidden", bgcolor: DESIGN.surface }}>
    <CardContent sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: "100%" }}>
          <Skeleton variant="rounded" width={40} height={40} sx={{ borderRadius: DESIGN.radiusSm }} />
          <Box sx={{ flexGrow: 1 }}>
            <Skeleton variant="text" width="55%" height={22} />
            <Skeleton variant="text" width="35%" height={16} sx={{ mt: 0.5 }} />
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 0.75 }}>
          {[1, 2, 3].map(i => <Skeleton key={i} variant="circular" width={36} height={36} />)}
        </Box>
      </Box>
      <Skeleton variant="rounded" height={8} sx={{ borderRadius: 4, mb: 1 }} />
      <Skeleton variant="rounded" height={8} width="80%" sx={{ borderRadius: 4 }} />
    </CardContent>
  </Card>
);

const FolderSkeleton = () => (
  <Card elevation={0} sx={{ borderRadius: DESIGN.radius, border: `1px solid ${DESIGN.border}`, bgcolor: DESIGN.surface, height: 148 }}>
    <CardContent sx={{ p: 3, display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Skeleton variant="rounded" width={48} height={48} sx={{ borderRadius: DESIGN.radiusSm }} />
      <Skeleton variant="text" width="60%" height={24} />
      <Skeleton variant="text" width="85%" height={16} />
    </CardContent>
  </Card>
);

const HeaderSkeleton = () => (
  <Box sx={{ mb: 4, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
    <Box>
      <Skeleton variant="text" width={280} height={44} />
      <Skeleton variant="text" width={380} height={22} sx={{ mt: 0.5 }} />
    </Box>
    <Skeleton variant="rounded" width={210} height={44} sx={{ borderRadius: DESIGN.radiusSm }} />
  </Box>
);

// ----------------------------------------------------------------------------------
// Breadcrumb
// ----------------------------------------------------------------------------------
const FolderBreadcrumb = ({ folderLabel, onBack, t }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 2.5 }}>
    <Button
      onClick={onBack}
      startIcon={<ArrowBack sx={{ fontSize: 16 }} />}
      sx={{
        textTransform: "none", fontWeight: 700, fontSize: "0.82rem",
        color: DESIGN.textSoft, px: 1, py: 0.5, borderRadius: DESIGN.radiusSm,
        "&:hover": { color: DESIGN.blue, bgcolor: DESIGN.accentSoft },
      }}
    >
      {t("document.title")}
    </Button>
    <ChevronRight sx={{ fontSize: 16, color: DESIGN.textSoft }} />
    <Typography sx={{ fontWeight: 800, fontSize: "0.82rem", color: DESIGN.text }}>
      {folderLabel}
    </Typography>
  </Box>
);

// ----------------------------------------------------------------------------------
// Grilla de carpetas
// ----------------------------------------------------------------------------------
const FolderCard = ({ folder, count, onClick, t }) => {
  const Icon = folder.icon;
  const label = t(`document.folders.${folder.key}.label`);
  const description = t(`document.folders.${folder.key}.description`);
  return (
    <motion.div layout initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} whileHover={{ y: -4 }} transition={{ duration: 0.22 }} style={{ height: "100%" }}>
      <Card
        elevation={0}
        onClick={onClick}
        sx={{
          cursor: "pointer",
          borderRadius: DESIGN.radius,
          border: `1px solid ${DESIGN.border}`,
          bgcolor: DESIGN.surface,
          boxShadow: DESIGN.shadow,
          transition: "all 0.25s ease",
          overflow: "hidden",
          position: "relative",
          width: "100%",
          height: "200px",
          display: "flex",
          flexDirection: "column",
          "&:hover": { boxShadow: DESIGN.shadowHover, borderColor: DESIGN.borderHover },
        }}
      >
        <CardContent sx={{ p: 3, flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
          <Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
              <Box sx={{
                width: 48, height: 48, borderRadius: DESIGN.radiusSm,
                bgcolor: `${folder.color}18`, border: `1px solid ${folder.color}33`,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <Icon sx={{ fontSize: 24, color: folder.color }} />
              </Box>
              <Box sx={{
                px: 1.1, py: 0.35, borderRadius: "999px",
                bgcolor: DESIGN.surfaceAlt, border: `1px solid ${DESIGN.border}`,
              }}>
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 800, color: DESIGN.textMid }}>
                  {count} {count === 1 ? "archivo" : "archivos"}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box sx={{ mt: 'auto' }}>
            <Typography sx={{ fontWeight: 800, fontSize: "0.95rem", color: DESIGN.text, mb: 0.5, minHeight: "1.2em", display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
              {label}
            </Typography>
            <Typography sx={{ fontSize: "0.78rem", color: DESIGN.textSoft, lineHeight: 1.5, minHeight: "2.4em", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
              {description}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );
};

const FolderGrid = ({ docsPorCarpeta, onOpenFolder, t }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: {
        xs: "1fr",
        sm: "repeat(2, 1fr)",
        md: "repeat(3, 1fr)",
      },
      gap: "20px", // equivalente a spacing={2.5} de Grid
    }}
  >
    {FOLDERS.map((folder) => (
      <FolderCard
        key={folder.key}
        folder={folder}
        count={docsPorCarpeta[folder.key] || 0}
        onClick={() => onOpenFolder(folder.key)}
        t={t}
      />
    ))}
  </Box>
);

const PDFFullscreenDialog = ({ open, onClose, pdfUrl, fileName, loading, isParteDiario }) => {
  const [scale, setScale] = useState(1.0);
  const [pageWidth, setPageWidth] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const contentRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const calc = () => {
      if (wrapperRef.current) {
        const available = wrapperRef.current.clientWidth * 0.85;
        setPageWidth(Math.max(400, available));
      }
    };
    setTimeout(calc, 100);
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, [open]);

  useEffect(() => {
    if (open) {
      setScale(1.0);
      setTimeout(() => contentRef.current?.focus(), 100);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleWheel = (e) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setScale((s) => parseFloat(Math.min(3.0, Math.max(0.25, s + delta)).toFixed(2)));
    };
    const handleKeyDown = (e) => {
      if (!e.ctrlKey) return;
      if (e.key === "+" || e.key === "=" || e.key === "-") {
        e.preventDefault();
        e.stopImmediatePropagation();
        setScale((s) =>
          e.key === "-"
            ? parseFloat(Math.max(0.25, s - 0.15).toFixed(2))
            : parseFloat(Math.min(3.0, s + 0.15).toFixed(2))
        );
      }
    };
    document.addEventListener("wheel", handleWheel, { passive: false, capture: true });
    document.addEventListener("keydown", handleKeyDown, { capture: true });
    return () => {
      document.removeEventListener("wheel", handleWheel, { capture: true });
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          background: `linear-gradient(160deg, ${DESIGN.navy} 0%, #0d1b3e 100%)`,
        },
      }}
    >
      <Box sx={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        px: 3, py: 1.5,
        background: "rgba(255,255,255,0.04)",
        borderBottom: "1px solid rgba(255,255,255,0.08)",
        backdropFilter: "blur(12px)",
        flexShrink: 0,
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: DESIGN.radiusSm,
            background: "rgba(0,229,255,0.12)",
            border: "1px solid rgba(0,229,255,0.25)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Description sx={{ fontSize: 18, color: DESIGN.accent }} />
          </Box>
          <Box>
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "0.95rem", letterSpacing: 0.3 }} noWrap>
              {fileName}
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.72rem", letterSpacing: 0.5 }}>
              PARTE DIARIO · VISTA PREVIA
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography sx={{ color: "rgba(255,255,255,0.3)", fontSize: "0.7rem", display: { xs: "none", md: "block" } }}>
            Ctrl + scroll · Ctrl +/−
          </Typography>

          <Box sx={{
            display: "flex", alignItems: "center", gap: 0.5,
            background: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: DESIGN.radiusSm,
            px: 0.75, py: 0.5,
          }}>
            <IconButton
              size="small"
              onClick={() => setScale((s) => parseFloat(Math.max(0.25, s - 0.15).toFixed(2)))}
              sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.08)" }, borderRadius: "6px" }}
            >
              <ZoomOut fontSize="small" />
            </IconButton>
            <Typography sx={{
              color: DESIGN.accent, fontSize: "0.8rem", fontWeight: 700,
              minWidth: 52, textAlign: "center", fontFamily: "monospace", letterSpacing: 0.5,
            }}>
              {Math.round(scale * 100)}%
            </Typography>
            <IconButton
              size="small"
              onClick={() => setScale((s) => parseFloat(Math.min(3.0, s + 0.15).toFixed(2)))}
              sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.08)" }, borderRadius: "6px" }}
            >
              <ZoomIn fontSize="small" />
            </IconButton>
          </Box>

          <Tooltip title="Restablecer zoom" placement="bottom">
            <IconButton
              onClick={() => setScale(1.0)}
              size="small"
              sx={{
                color: "rgba(255,255,255,0.5)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: DESIGN.radiusSm,
                width: 36, height: 36, fontSize: "0.65rem", fontWeight: 700,
                "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.08)" },
              }}
            >
              <Typography sx={{ fontSize: "0.62rem", fontWeight: 800, color: "inherit", lineHeight: 1 }}>
                100%
              </Typography>
            </IconButton>
          </Tooltip>

          <IconButton
            onClick={onClose}
            sx={{
              color: "rgba(255,255,255,0.6)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: DESIGN.radiusSm,
              width: 36, height: 36,
              "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.08)", borderColor: "rgba(255,255,255,0.25)" },
              transition: "all 0.2s",
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <DialogContent
        ref={contentRef}
        tabIndex={0}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          overflowY: "auto",
          p: { xs: 2, md: 4 },
          outline: "none",
          background: "transparent",
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-track": { background: "rgba(255,255,255,0.03)" },
          "&::-webkit-scrollbar-thumb": { background: "rgba(255,255,255,0.15)", borderRadius: 3 },
        }}
      >
        <Box
          ref={wrapperRef}
          sx={{ width: "100%", display: "flex", justifyContent: "center", alignItems: "flex-start" }}
        >
          {loading ? (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", pt: 12, gap: 2 }}>
              <Box sx={{ position: "relative", width: 64, height: 64 }}>
                <CircularProgress size={64} thickness={2} sx={{ color: "rgba(255,255,255,0.08)", position: "absolute" }} variant="determinate" value={100} />
                <CircularProgress size={64} thickness={2} sx={{ color: DESIGN.accent, position: "absolute" }} />
              </Box>
              <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.85rem", letterSpacing: 1 }}>
                CARGANDO DOCUMENTO
              </Typography>
            </Box>
          ) : pdfUrl && pageWidth ? (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
              <Box sx={{
                userSelect: "none",
                borderRadius: DESIGN.radius,
                overflow: "hidden",
                boxShadow: "0 32px 80px rgba(0,0,0,0.6)",
              }}>
                <Document
                  file={pdfUrl}
                  onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                  loading={
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 8 }}>
                      <CircularProgress sx={{ color: DESIGN.accent }} />
                    </Box>
                  }
                >
                  {numPages ? Array.from(new Array(isParteDiario ? Math.min(1, numPages) : numPages), (el, index) => (
                    <Box key={`page_${index + 1}`} sx={{ mb: 2, display: "flex", justifyContent: "center" }}>
                      <Page
                        pageNumber={index + 1}
                        width={pageWidth * scale}
                        renderAnnotationLayer={false}
                        renderTextLayer={false}
                      />
                    </Box>
                  )) : null}
                </Document>
              </Box>
            </motion.div>
          ) : null}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const usePdfCache = () => {
  const cacheRef = useRef({});
  const getPdfUrl = useCallback((fileName) => cacheRef.current[fileName] || null, []);
  const setPdfUrl = useCallback((fileName, url) => { cacheRef.current[fileName] = url; }, []);
  const clearCache = useCallback(() => { cacheRef.current = {}; }, []);
  return { getPdfUrl, setPdfUrl, clearCache };
};

const DesarrolloTrabajoViewer = ({ rows = [], totalHs }) => {
  if (!rows || rows.length === 0)
    return (
      <Typography sx={{ fontSize: "0.82rem", color: DESIGN.textSoft, fontStyle: "italic" }}>
        Sin actividades registradas
      </Typography>
    );
  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "grid", gridTemplateColumns: "70px 70px 1fr 60px", gap: "6px", mb: 0.75, px: 1 }}>
        {["Inicio", "Fin", "Actividad", "Cargo"].map((h) => (
          <Typography key={h} sx={{ fontSize: "0.65rem", fontWeight: 700, color: DESIGN.textSoft, textTransform: "uppercase", letterSpacing: 0.5 }}>
            {h}
          </Typography>
        ))}
      </Box>
      {rows.map((row, i) => (
        <Box key={i} sx={{
          display: "grid", gridTemplateColumns: "70px 70px 1fr 60px", gap: "6px",
          px: 1, py: 0.65, borderRadius: "6px",
          bgcolor: i % 2 === 0 ? "rgba(21,101,192,0.05)" : "transparent",
          "&:hover": { bgcolor: "rgba(21,101,192,0.09)" }, transition: "background 0.15s",
        }}>
          <Typography sx={{ fontSize: "0.78rem", color: DESIGN.textMid, fontFamily: "monospace" }}>{row.inicio || "—"}</Typography>
          <Typography sx={{ fontSize: "0.78rem", color: DESIGN.textMid, fontFamily: "monospace" }}>{row.fin || "—"}</Typography>
          <Typography sx={{ fontSize: "0.78rem", color: DESIGN.text, fontWeight: 500 }}>{row.actividad || "—"}</Typography>
          <Typography sx={{ fontSize: "0.78rem", color: DESIGN.blue, fontWeight: 700, textAlign: "right", fontFamily: "monospace" }}>{row.cargo || "—"}</Typography>
        </Box>
      ))}
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1, pt: 1, borderTop: `1px solid ${DESIGN.border}`, px: 1 }}>
        <Typography sx={{ fontSize: "0.7rem", color: DESIGN.textSoft, fontWeight: 600 }}>
          {rows.length} actividad{rows.length !== 1 ? "es" : ""}
        </Typography>
        <Typography sx={{ fontSize: "0.7rem", color: DESIGN.blue, fontWeight: 700 }}>
          Total: {totalHs ?? rows.reduce((acc, r) => acc + (parseFloat(String(r.cargo || '0').replace(',', '.')) || 0), 0).toFixed(1)} hs
        </Typography>
      </Box>
    </Box>
  );
};

const DesarrolloTrabajoEditor = ({ rows = [], onChange }) => {
  const handleRowChange = (idx, field, val) =>
    onChange(rows.map((r, i) => i === idx ? { ...r, [field]: val } : r));
  const handleAddRow = () =>
    onChange([...rows, { inicio: "", fin: "", total: "", actividad: "", cargo: "" }]);
  const handleRemoveRow = (idx) =>
    onChange(rows.filter((_, i) => i !== idx));

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "6px", fontSize: "0.75rem",
      "&:hover fieldset": { borderColor: DESIGN.blue },
      "&.Mui-focused fieldset": { borderColor: DESIGN.blue },
    },
    "& .MuiInputLabel-root.Mui-focused": { color: DESIGN.blue },
  };

  return (
    <Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "80px 80px 1fr 70px 28px", gap: "5px", mb: 0.75, px: 0.5 }}>
        {["Inicio", "Fin", "Actividad", "Cargo", ""].map((h, i) => (
          <Typography key={i} sx={{ fontSize: "0.62rem", fontWeight: 700, color: DESIGN.textSoft, textTransform: "uppercase", letterSpacing: 0.4 }}>
            {h}
          </Typography>
        ))}
      </Box>
      {rows.map((row, idx) => (
        <Box key={idx} sx={{ display: "grid", gridTemplateColumns: "80px 80px 1fr 70px 28px", gap: "5px", mb: 0.75, alignItems: "center" }}>
          <TextField size="small" value={row.inicio || ""} onChange={(e) => handleRowChange(idx, "inicio", e.target.value)} placeholder="08:20:00" sx={inputSx} inputProps={{ style: { fontFamily: "monospace", fontSize: "0.75rem" } }} />
          <TextField size="small" value={row.fin || ""} onChange={(e) => handleRowChange(idx, "fin", e.target.value)} placeholder="10:00:00" sx={inputSx} inputProps={{ style: { fontFamily: "monospace", fontSize: "0.75rem" } }} />
          <TextField size="small" value={row.actividad || ""} onChange={(e) => handleRowChange(idx, "actividad", e.target.value)} placeholder="Descripción..." sx={inputSx} />
          <TextField size="small" value={row.cargo || ""} onChange={(e) => handleRowChange(idx, "cargo", e.target.value)} placeholder="2" sx={inputSx} inputProps={{ style: { fontFamily: "monospace", fontSize: "0.75rem", textAlign: "right" } }} />
          <IconButton size="small" onClick={() => handleRemoveRow(idx)} sx={{ width: 26, height: 26, borderRadius: "6px", color: "#ef4444", border: "1px solid rgba(239,68,68,0.2)", "&:hover": { bgcolor: "rgba(239,68,68,0.08)" }, p: 0 }}>
            <Close sx={{ fontSize: 13 }} />
          </IconButton>
        </Box>
      ))}
      <Button size="small" onClick={handleAddRow} variant="outlined" sx={{ mt: 0.5, fontSize: "0.73rem", textTransform: "none", fontWeight: 600, color: DESIGN.blue, borderColor: DESIGN.border, borderRadius: "6px", py: 0.4, "&:hover": { bgcolor: DESIGN.accentSoft, borderColor: DESIGN.blue } }}>
        + Agregar fila
      </Button>
    </Box>
  );
};

const CamposDrawer = ({
  open, onClose, doc, editMode, setEditMode,
  editableCampos, handleCampoChange, handleSave, loadingSave,
  onCancelEdit, t, isSuperAdmin
}) => {
  const CAMPOS_TEXTO_LARGO = ["observaciones", "observaciones_tecnicas", "observaciones_geologicas"];

  const renderCampoLectura = (key) => {
    const value = editableCampos[key];
    const isEmpty = value === undefined || value === null || value === "" ||
      (Array.isArray(value) && value.length === 0);

    if (key === "desarrollo_trabajo") {
      return (
        <Box key={key} sx={{ mb: 2.5, pb: 2.5, borderBottom: `1px solid ${DESIGN.border}` }}>
          <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: DESIGN.textSoft, textTransform: "uppercase", letterSpacing: 0.5, mb: 1 }}>
            {t(ETIQUETAS_CAMPOS[key]) || key}
          </Typography>
          <DesarrolloTrabajoViewer
            rows={Array.isArray(value) ? value : (value?.actividades ?? [])}
            totalHs={value?.total_hs}
          />
        </Box>
      );
    }

    if (CAMPOS_TEXTO_LARGO.includes(key)) {
      return (
        <Box key={key} sx={{ mb: 2.5, pb: 2.5, borderBottom: `1px solid ${DESIGN.border}`, "&:last-child": { borderBottom: "none" } }}>
          <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: DESIGN.textSoft, textTransform: "uppercase", letterSpacing: 0.5, mb: 0.75 }}>
            {t(ETIQUETAS_CAMPOS[key]) || key}
          </Typography>
          <Typography sx={{ fontSize: "0.82rem", color: isEmpty ? DESIGN.textSoft : DESIGN.text, lineHeight: 1.7, whiteSpace: "pre-wrap", fontStyle: isEmpty ? "italic" : "normal" }}>
            {isEmpty ? "Sin observaciones" : String(value)}
          </Typography>
        </Box>
      );
    }

    return (
      <Box key={key} sx={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        mb: 0.85, pb: 0.85, borderBottom: `1px solid ${DESIGN.border}`,
        "&:last-child": { borderBottom: "none" }, gap: 1, zIndex: 99999,
      }}>
        <Typography sx={{ fontSize: "0.7rem", color: DESIGN.textSoft, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, minWidth: 120, flexShrink: 0 }}>
          {t(ETIQUETAS_CAMPOS[key]) || key}
        </Typography>
        <Typography sx={{ fontSize: "0.83rem", color: isEmpty ? DESIGN.textSoft : DESIGN.text, fontWeight: isEmpty ? 400 : 500, textAlign: "right", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 220 }}>
          {isEmpty ? "—" : String(value)}
        </Typography>
      </Box>
    );
  };

  const renderCampoEdicion = (key) => {
    const value = editableCampos[key];

    if (key === "desarrollo_trabajo") {
      return (
        <Box key={key} sx={{ mb: 2.5, pb: 2.5, borderBottom: `1px solid ${DESIGN.border}` }}>
          <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: DESIGN.textSoft, textTransform: "uppercase", letterSpacing: 0.5, mb: 1 }}>
            {t(ETIQUETAS_CAMPOS[key]) || key}
          </Typography>
          <DesarrolloTrabajoEditor
            rows={Array.isArray(value) ? value : (value?.actividades ?? [])}
            onChange={(newRows) => handleCampoChange("desarrollo_trabajo", newRows)}
          />
        </Box>
      );
    }

    if (CAMPOS_TEXTO_LARGO.includes(key)) {
      return (
        <TextField key={key} fullWidth size="small"
          label={t(ETIQUETAS_CAMPOS[key]) || key}
          value={value ?? ""} multiline rows={3}
          placeholder={t(PLACEHOLDERS[key]) || ""}
          onChange={(e) => handleCampoChange(key, e.target.value)}
          sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.82rem", "&:hover fieldset": { borderColor: DESIGN.blue }, "&.Mui-focused fieldset": { borderColor: DESIGN.blue } }, "& .MuiInputLabel-root.Mui-focused": { color: DESIGN.blue } }}
        />
      );
    }

    return (
      <TextField key={key} fullWidth size="small"
        label={t(ETIQUETAS_CAMPOS[key]) || key}
        value={Array.isArray(value) ? "" : (value ?? "")}
        placeholder={t(PLACEHOLDERS[key]) || ""}
        onChange={(e) => handleCampoChange(key, e.target.value)}
        sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.82rem", "&:hover fieldset": { borderColor: DESIGN.blue }, "&.Mui-focused fieldset": { borderColor: DESIGN.blue } }, "& .MuiInputLabel-root.Mui-focused": { color: DESIGN.blue } }}
      />
    );
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: 99999 }}
      PaperProps={{
        sx: {
          width: { xs: "100vw", sm: 480, md: 520 },
          bgcolor: DESIGN.surface,
          boxShadow: "-8px 0 40px rgba(10,15,46,0.15)",
          display: "flex", flexDirection: "column",
        },
      }}
    >
      <Box sx={{
        px: 3, py: 2,
        borderBottom: `1px solid ${DESIGN.border}`,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexShrink: 0,
        background: `linear-gradient(135deg, ${DESIGN.navy} 0%, #0d1b3e 100%)`,
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: DESIGN.radiusSm,
            background: "rgba(0,229,255,0.12)", border: "1px solid rgba(0,229,255,0.25)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Description sx={{ fontSize: 18, color: DESIGN.accent }} />
          </Box>
          <Box>
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "0.9rem", letterSpacing: 0.3 }} noWrap>
              {doc?.fileName?.replace(/\.pdf$/i, "") || ""}
            </Typography>


            <Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.68rem", letterSpacing: 0.5, textTransform: "uppercase" }}>
              {editMode ? "✏️ Modo edición" : "Campos OCR"}
            </Typography>

          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 0.75, alignItems: "center" }}>
          {isSuperAdmin && !editMode && (
            <IconButton
              onClick={() => setEditMode(true)} size="small"
              sx={{ width: 34, height: 34, borderRadius: "8px", color: "rgba(255,255,255,0.7)", border: "1px solid rgba(255,255,255,0.15)", "&:hover": { bgcolor: "rgba(255,255,255,0.08)", color: "#fff" } }}
            >
              <Edit sx={{ fontSize: 17 }} />
            </IconButton>
          )}

          <IconButton
            onClick={onClose} size="small"
            sx={{ width: 34, height: 34, borderRadius: "8px", color: "rgba(255,255,255,0.6)", border: "1px solid rgba(255,255,255,0.1)", "&:hover": { bgcolor: "rgba(255,255,255,0.08)", color: "#fff" } }}
          >
            <Close sx={{ fontSize: 17 }} />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{
        flex: 1, overflowY: "auto", px: 3, py: 2.5,
        "&::-webkit-scrollbar": { width: 4 },
        "&::-webkit-scrollbar-track": { background: "transparent" },
        "&::-webkit-scrollbar-thumb": { background: DESIGN.border, borderRadius: 2 },
      }}>
        {CAMPOS_ESPERADOS.map((key) =>
          editMode ? renderCampoEdicion(key) : renderCampoLectura(key)
        )}
      </Box>

      {editMode && (
        <Box sx={{
          px: 3, py: 2, flexShrink: 0,
          borderTop: `1px solid ${DESIGN.border}`,
          display: "flex", gap: 1, justifyContent: "flex-end",
          bgcolor: DESIGN.surfaceAlt,
        }}>
          <Button size="small" onClick={onCancelEdit} sx={{ fontSize: "0.78rem", textTransform: "none", color: DESIGN.textSoft }}>
            Cancelar
          </Button>
          <Button
            variant="contained" onClick={handleSave} disabled={loadingSave}
            startIcon={loadingSave ? <CircularProgress size={14} color="inherit" /> : <Save sx={{ fontSize: 16 }} />}
            sx={{
              fontSize: "0.82rem", textTransform: "none", fontWeight: 700,
              borderRadius: DESIGN.radiusSm, px: 2.5,
              background: `linear-gradient(135deg, ${DESIGN.blue}, ${DESIGN.blueBright})`,
              boxShadow: "none", "&:hover": { background: "linear-gradient(135deg, #1251a3, #1f5fcc)" },
            }}
          >
            Guardar cambios
          </Button>
        </Box>
      )}
    </Drawer>
  );
};

const DocumentCard = React.memo(({
  doc, onDownload, onToggle, isOpen, onSaved,
  isSuperAdmin, isTestingRole, showPdfOnly, pdfCache, isBlurred = false,
  wellId
}) => {
  const { t } = useTranslation();
  const [editMode, setEditMode] = useState(false);
  const [editableCampos, setEditableCampos] = useState(doc.campos || {});
  const [loadingSave, setLoadingSave] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [pdfUrl, setPdfUrl] = useState(null);
  const [existsInGCS, setExistsInGCS] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const apiUrl = process.env.REACT_APP_API_URL;

  const formattedDate = useMemo(() =>
    doc.createdAt ? dayjs(doc.createdAt).format("DD/MM/YYYY HH:mm") : "",
    [doc.createdAt]
  );

  const canEdit = isSuperAdmin || isTestingRole;

  useEffect(() => {
    setEditableCampos(doc.campos || {});
    setEditMode(false);
  }, [doc]);

  const handleCloseDrawer = useCallback(() => {
    setDrawerOpen(false);
    setEditMode(false);
    setEditableCampos(doc.campos || {});
  }, [doc]);

  const handleCancelEdit = useCallback(() => {
    setEditMode(false);
    setEditableCampos(doc.campos || {});
  }, [doc]);

  useEffect(() => {
    let cancelled = false;
    const checkIfExists = async (attempt = 0) => {
      try {
        const token = localStorage.getItem("auth_token") || sessionStorage.getItem("auth_token");
        const res = await axios.get(
          `${apiUrl}/api/exists/${encodeURIComponent(doc.fileName)}`,
          { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );
        if (!cancelled) setExistsInGCS(res.data.exists);
        if (!res.data.exists && attempt < 3) setTimeout(() => checkIfExists(attempt + 1), 1500);
      } catch (err) { console.error(err); }
    };
    if (doc.fileName && !isBlurred) checkIfExists();
    return () => { cancelled = true; };
  }, [doc.fileName, apiUrl, isBlurred]);

  const handleCampoChange = useCallback((key, value) => {
    if (!isBlurred) setEditableCampos((prev) => ({ ...prev, [key]: value }));
  }, [isBlurred]);

  const handleSave = useCallback(async () => {
    if (isBlurred) return;
    setLoadingSave(true);
    try {
      const token = localStorage.getItem("auth_token") || sessionStorage.getItem("auth_token");

      const camposNormalizados = { ...editableCampos };
      const dt = camposNormalizados.desarrollo_trabajo;

      if (Array.isArray(dt)) {
        camposNormalizados.desarrollo_trabajo = { actividades: dt, total_hs: 0 };
      } else if (!dt || typeof dt !== "object" || !Array.isArray(dt.actividades)) {
        camposNormalizados.desarrollo_trabajo = { actividades: [], total_hs: 0 };
      }

      await axios.put(
        `${apiUrl}/api/ocr-results-db/${doc._id}`,
        { campos: camposNormalizados, pozo: wellId },
        { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );

      setSnackbar({ open: true, message: t("document.saveSuccess"), severity: "success" });
      setEditMode(false);
      setDrawerOpen(false);
      onSaved();
    } catch {
      setSnackbar({ open: true, message: t("document.saveError"), severity: "error" });
    } finally { setLoadingSave(false); }
  }, [apiUrl, doc._id, editableCampos, onSaved, t, isBlurred, wellId]);
  const handlePreviewClick = useCallback(async () => {
    if (isBlurred) return;
    setFullscreenOpen(true);
    const cachedUrl = pdfCache.getPdfUrl(doc.fileName);
    if (cachedUrl) { setPdfUrl(cachedUrl); return; }
    setPdfLoading(true);
    try {
      const token = localStorage.getItem("auth_token") || sessionStorage.getItem("auth_token");
      const res = await axios.get(
        `${apiUrl}/api/get-pdf-url?fileName=${doc.fileName}`,
        { responseType: "blob", withCredentials: true, params: { pozo: wellId }, timeout: 10000, headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      pdfCache.setPdfUrl(doc.fileName, url);
      setPdfUrl(url);
    } catch (err) {
      let msg = "Error al cargar PDF.";
      if (err.code === "ECONNABORTED") msg = "Tiempo de espera agotado.";
      else if (err.response?.status === 404) msg = "Archivo no encontrado.";
      setSnackbar({ open: true, message: msg, severity: "error" });
      setFullscreenOpen(false);
    } finally { setPdfLoading(false); }
  }, [apiUrl, pdfCache, doc.fileName, isBlurred, wellId]);

  const handleSnackbarClose = useCallback(() => setSnackbar((p) => ({ ...p, open: false })), []);
  const handleDownload = useCallback(() => { if (!isBlurred) onDownload(doc.gcsUrl); }, [doc.gcsUrl, onDownload, isBlurred]);

  const fileLabel = useMemo(() => {
    let name = doc.fileName?.replace(/\.pdf$/i, "") || "";

    name = name.replace(/parte\s+diario/i, "Par. Diario");

    return name.length > 25
      ? name.slice(0, 25) + "…"
      : name;
  }, [doc.fileName]);

  const statusColor = existsInGCS === null ? "#94a3b8" : existsInGCS ? "#22c55e" : "#ef4444";
  const statusLabel = existsInGCS === null ? "Verificando…" : existsInGCS ? t("document.found") : t("document.notFound");

  const camposPreview = useMemo(() => {
    const c = doc.campos || {};
    return [
      c.fecha && { label: "Fecha", value: c.fecha },
      c.pozo && { label: "Pozo", value: c.pozo },
    ].filter(Boolean).slice(0, 3);
  }, [doc.campos]);

  return (
    <>
      <motion.div
        layout
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={!isBlurred ? { y: -3 } : {}}
        transition={{ duration: 0.25 }}
      >
        <Card
          elevation={0}
          sx={{
            borderRadius: DESIGN.radius,
            border: `1px solid ${drawerOpen ? DESIGN.borderHover : DESIGN.border}`,
            bgcolor: DESIGN.surface,
            transition: "all 0.25s ease",
            boxShadow: drawerOpen ? DESIGN.shadowHover : DESIGN.shadow,
            filter: isBlurred ? "blur(3px)" : "none",
            opacity: isBlurred ? 0.6 : 1,
            pointerEvents: isBlurred ? "none" : "auto",
            overflow: "hidden",
            position: "relative",
            "&::before": drawerOpen ? {
              content: '""', position: "absolute", top: 0, left: 0, right: 0, height: "3px",
              background: `linear-gradient(90deg, ${DESIGN.blue}, ${DESIGN.blueBright}, ${DESIGN.accent})`,
            } : {},
          }}
        >
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: "20px 8px 16px" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                  <Box sx={{
                    width: 44, height: 44, flexShrink: 0, borderRadius: DESIGN.radiusSm,
                    background: DESIGN.accentSoft, border: `1px solid rgba(41,121,255,0.18)`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <Description sx={{ fontSize: 22, color: DESIGN.blue }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: "12px", color: DESIGN.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {fileLabel}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.4 }}>
                      <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: statusColor, flexShrink: 0, boxShadow: `0 0 6px ${statusColor}` }} title={statusLabel} />
                      <Typography sx={{ fontSize: "0.72rem", color: DESIGN.textSoft }}>
                        {t("document.uploaded")} · {formattedDate}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                  <Tooltip title={pdfLoading ? "Cargando…" : t("document.preview")} placement="top">
                    <IconButton onClick={handlePreviewClick} disabled={pdfLoading} size="small"
                      sx={{ width: 34, height: 34, borderRadius: "8px", bgcolor: pdfLoading ? "transparent" : DESIGN.accentSoft, border: `1px solid ${pdfLoading ? "transparent" : "rgba(41,121,255,0.18)"}`, color: DESIGN.blue, "&:hover": { bgcolor: "rgba(41,121,255,0.15)", borderColor: DESIGN.blue }, transition: "all 0.2s" }}>
                      {pdfLoading ? <CircularProgress size={16} sx={{ color: DESIGN.blue }} /> : <Description sx={{ fontSize: 17 }} />}
                    </IconButton>
                  </Tooltip>

                  {(!doc.categoria || doc.categoria === "partes_diarios") && (
                    <>
                      <Tooltip title={drawerOpen ? "Cerrar campos" : t("document.showOCR")} placement="top">
                        <IconButton onClick={() => { if (!isBlurred) setDrawerOpen(true); }} size="small"
                          sx={{ width: 34, height: 34, borderRadius: "8px", bgcolor: drawerOpen ? "rgba(139,92,246,0.1)" : "transparent", border: `1px solid ${drawerOpen ? "rgba(139,92,246,0.3)" : DESIGN.border}`, color: drawerOpen ? "#8b5cf6" : DESIGN.textSoft, "&:hover": { bgcolor: "rgba(139,92,246,0.12)", borderColor: "rgba(139,92,246,0.4)" }, transition: "all 0.2s" }}>
                          <Visibility sx={{ fontSize: 17 }} />
                        </IconButton>
                      </Tooltip>

                      {canEdit && (
                        <Tooltip title={t("document.editFields")} placement="top">
                          <IconButton onClick={() => { if (!isBlurred) { setDrawerOpen(true); setEditMode(true); } }} size="small"
                            sx={{ width: 34, height: 34, borderRadius: "8px", bgcolor: "transparent", border: `1px solid ${DESIGN.border}`, color: DESIGN.textSoft, "&:hover": { bgcolor: "rgba(21,101,192,0.08)", borderColor: DESIGN.blue, color: DESIGN.blue }, transition: "all 0.2s" }}>
                            <Edit sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  )}

                  <Tooltip title={t("document.download")} placement="top">
                    <IconButton onClick={handleDownload} size="small"
                      sx={{ width: 34, height: 34, borderRadius: "8px", bgcolor: "transparent", border: `1px solid ${DESIGN.border}`, color: DESIGN.textSoft, "&:hover": { bgcolor: "rgba(21,101,192,0.08)", borderColor: DESIGN.blue, color: DESIGN.blue }, transition: "all 0.2s" }}>
                      <CloudDownload sx={{ fontSize: 17 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Box>

            {camposPreview.length > 0 && (
              <>
                <Box sx={{ height: "1px", bgcolor: DESIGN.border, mx: 2.5 }} />
                <Box sx={{ px: 2.5, py: 1.5, display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                  {camposPreview.map(({ label, value }) => (
                    <Box key={label} sx={{
                      display: "flex", alignItems: "center", gap: 0.5,
                      px: 1.25, py: 0.5, borderRadius: "6px",
                      bgcolor: DESIGN.surfaceAlt, border: `1px solid ${DESIGN.border}`,
                    }}>
                      <Typography sx={{ fontSize: "0.65rem", color: DESIGN.textSoft, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>
                        {label}
                      </Typography>
                      <Typography sx={{ fontSize: "0.72rem", color: DESIGN.text, fontWeight: 600 }}>
                        {value}
                      </Typography>
                    </Box>
                  ))}
                  <Box
                    onClick={() => { if (!isBlurred) setDrawerOpen(true); }}
                    sx={{
                      display: "flex", alignItems: "center", gap: 0.5,
                      px: 1.25, py: 0.5, borderRadius: "6px", cursor: "pointer",
                      bgcolor: DESIGN.accentSoft, border: `1px solid rgba(41,121,255,0.18)`,
                      "&:hover": { bgcolor: "rgba(41,121,255,0.12)", borderColor: DESIGN.blue },
                      transition: "all 0.2s",
                    }}
                  >
                    <Typography sx={{ fontSize: "0.7rem", color: DESIGN.blue, fontWeight: 700 }}>
                      Ver todos →
                    </Typography>
                  </Box>
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      </motion.div>

      <CamposDrawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        doc={doc}
        editMode={editMode}
        setEditMode={setEditMode}
        editableCampos={editableCampos}
        handleCampoChange={handleCampoChange}
        handleSave={handleSave}
        loadingSave={loadingSave}
        onCancelEdit={handleCancelEdit}
        t={t}
        isSuperAdmin={isSuperAdmin}
      />

      <PDFFullscreenDialog
        open={fullscreenOpen}
        onClose={() => setFullscreenOpen(false)}
        pdfUrl={pdfUrl}
        fileName={doc.fileName}
        loading={pdfLoading}
        isParteDiario={!doc.categoria || doc.categoria === "partes_diarios"}
      />

      {!isBlurred && (
        <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
          <Alert onClose={handleSnackbarClose} severity={snackbar.severity} variant="filled" sx={{ borderRadius: DESIGN.radiusSm, fontWeight: 500 }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      )}
    </>
  );
});

const NonBlockingOverlay = ({ children }) => (
  <Box sx={{
    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 1200, pointerEvents: "none",
  }}>
    <Box sx={{ pointerEvents: "auto" }}>{children}</Box>
  </Box>
);

// ----------------------------------------------------------------------------------
// Upload button: single button with folder-picker dropdown
// ----------------------------------------------------------------------------------
const UploadFolderButton = ({ uploading, disabled, onFolderSelected, t, carpetaActiva }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleOpen = (e) => {
    if (!disabled && !uploading) {
      if (carpetaActiva) {
        onFolderSelected(carpetaActiva);
      } else {
        setAnchorEl(e.currentTarget);
      }
    }
  };
  const handleClose = () => setAnchorEl(null);
  const handleSelect = (folderKey) => {
    handleClose();
    onFolderSelected(folderKey);
  };

  return (
    <>
      <Button
        data-tour="upload-button"
        variant="outlined"
        startIcon={uploading ? <CircularProgress size={14} /> : <UploadFile fontSize="small" />}
        endIcon={!carpetaActiva ? <KeyboardArrowDown sx={{ fontSize: 18, transition: "transform 0.2s", transform: open ? "rotate(180deg)" : "none" }} /> : null}
        onClick={handleOpen}
        disabled={disabled || uploading}
        sx={{
          height: 44, px: 2.5, borderRadius: DESIGN.radiusSm,
          fontWeight: 600, fontSize: "0.82rem", textTransform: "none",
          borderColor: DESIGN.border, color: DESIGN.textMid,
          "&:hover": { borderColor: DESIGN.blue, color: DESIGN.blue, bgcolor: DESIGN.accentSoft },
          transition: "all 0.2s",
        }}
      >
        {uploading ? t("document.uploading") : t("document.uploadButton")}
      </Button>

      {!carpetaActiva && (
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          elevation: 8,
          sx: {
            mt: 0.75, minWidth: 280, borderRadius: DESIGN.radius,
            border: `1px solid ${DESIGN.border}`,
            boxShadow: DESIGN.shadowHover,
            overflow: "visible",
            "&::before": {
              content: '""', position: "absolute", top: -6, right: 16,
              width: 12, height: 12, bgcolor: "background.paper",
              border: `1px solid ${DESIGN.border}`, borderBottom: "none", borderRight: "none",
              transform: "rotate(45deg)",
            },
          },
        }}
      >
        <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
          <Typography sx={{ fontSize: "0.68rem", fontWeight: 800, color: DESIGN.textSoft, textTransform: "uppercase", letterSpacing: 0.6 }}>
            {t("document.uploadSelectFolder")}
          </Typography>
        </Box>
        <Divider sx={{ mb: 0.5 }} />
        {FOLDERS.map((folder) => {
          const FolderIcon = folder.icon;
          const label = t(`document.folders.${folder.key}.label`);
          return (
            <MenuItem
              key={folder.key}
              onClick={() => handleSelect(folder.key)}
              sx={{
                py: 1.25, px: 2, borderRadius: "8px", mx: 0.75, mb: 0.25,
                "&:hover": { bgcolor: `${folder.color}12` },
              }}
            >
              <ListItemIcon>
                <Box sx={{
                  width: 32, height: 32, borderRadius: "8px",
                  bgcolor: `${folder.color}18`, border: `1px solid ${folder.color}33`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <FolderIcon sx={{ fontSize: 17, color: folder.color }} />
                </Box>
              </ListItemIcon>
              <ListItemText
                primary={label}
                primaryTypographyProps={{ fontWeight: 600, fontSize: "0.875rem", color: DESIGN.text }}
              />
            </MenuItem>
          );
        })}
      </Menu>
      )}
    </>
  );
};

const DownloadSplitButton = ({ onDownloadZip, loading, disabled, count, t }) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const menuOpen = Boolean(menuAnchor);

  return (
    <>
      <ButtonGroup
        variant="contained"
        disableElevation
        disabled={disabled || loading}
        sx={{
          height: 44,
          borderRadius: DESIGN.radiusSm,
          overflow: "hidden",
          "& .MuiButtonGroup-grouped": {
            borderColor: "rgba(255,255,255,0.2) !important",
          },

        }}
      >
        <Button
          onClick={onDownloadZip}
          startIcon={loading ? <CircularProgress size={15} color="inherit" /> : <FolderZip fontSize="small" />}
          sx={{
            px: 2.5, fontWeight: 700, letterSpacing: 0.4, fontSize: "0.82rem",
            background: `linear-gradient(135deg, ${DESIGN.blue} 0%, ${DESIGN.blueBright} 100%)`,
            "&:hover": { background: `linear-gradient(135deg, #1251a3 0%, #1f5fcc 100%)` },
            textTransform: "none",
            "&.Mui-disabled": {
              cursor: "not-allowed",
              pointerEvents: "auto",
              background: `linear-gradient(135deg, ${DESIGN.blue} 0%, ${DESIGN.blueBright} 100%)`,
              color: "#fff",
            },
          }}
        >
          {loading ? t("document.downloading") : `${t("document.downloadAll")} (${count})`}
        </Button>
        <Button
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          sx={{
            px: 1.25, minWidth: "40px !important",
            background: `linear-gradient(135deg, ${DESIGN.blue} 0%, ${DESIGN.blueBright} 100%)`,
            "&:hover": { background: `linear-gradient(135deg, #1251a3 0%, #1f5fcc 100%)` },
          }}
          aria-controls={menuOpen ? "download-menu" : undefined}
          aria-haspopup="true"
        >
          <KeyboardArrowDown sx={{ fontSize: 20, transition: "transform 0.2s", transform: menuOpen ? "rotate(180deg)" : "none" }} />
        </Button>
      </ButtonGroup>

      <Menu
        id="download-menu"
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          elevation: 8,
          sx: {
            mt: 0.75, minWidth: 280, borderRadius: DESIGN.radius,
            border: `1px solid ${DESIGN.border}`,
            boxShadow: DESIGN.shadowHover,
            overflow: "visible",
            "&::before": {
              content: '""', position: "absolute", top: -6, right: 16,
              width: 12, height: 12, bgcolor: "background.paper",
              border: `1px solid ${DESIGN.border}`, borderBottom: "none", borderRight: "none",
              transform: "rotate(45deg)",
            },
          },
        }}
      >
        <MenuItem onClick={() => { setMenuAnchor(null); onDownloadZip(); }} sx={{ py: 1.5, px: 2, borderRadius: "8px", mx: 0.75, mt: 0.5 }}>
          <ListItemIcon><FolderZip sx={{ color: DESIGN.blue }} /></ListItemIcon>
          <ListItemText
            primary={t("document.downloadAllZip")}
            primaryTypographyProps={{ fontWeight: 600, fontSize: "0.875rem", color: DESIGN.text }}
          />
        </MenuItem>

        <Divider sx={{ my: 0.5, mx: 1 }} />

        <Tooltip title={t("document.comingSoon")} placement="left">
          <span>
            <MenuItem disabled sx={{ py: 1.5, px: 2, borderRadius: "8px", mx: 0.75, opacity: 0.45 }}>
              <ListItemIcon><TableChart sx={{ color: "#22c55e" }} /></ListItemIcon>
              <ListItemText
                primary={t("document.downloadExcel")}
                secondary={t("document.comingSoon")}
                primaryTypographyProps={{ fontWeight: 600, fontSize: "0.875rem" }}
                secondaryTypographyProps={{ fontSize: "0.72rem" }}
              />
            </MenuItem>
          </span>
        </Tooltip>

        <Tooltip title={t("document.comingSoon")} placement="left">
          <span>
            <MenuItem disabled sx={{ py: 1.5, px: 2, borderRadius: "8px", mx: 0.75, mb: 0.5, opacity: 0.45 }}>
              <ListItemIcon><AutoAwesome sx={{ color: "#f59e0b" }} /></ListItemIcon>
              <ListItemText
                primary={t("document.downloadIAAnalysis")}
                secondary={t("document.comingSoon")}
                primaryTypographyProps={{ fontWeight: 600, fontSize: "0.875rem" }}
                secondaryTypographyProps={{ fontSize: "0.72rem" }}
              />
            </MenuItem>
          </span>
        </Tooltip>
      </Menu>
    </>
  );
};

const Documentos = () => {
  const { t } = useTranslation();
  const { auth, refreshAuth } = useAuth();
  const { wellId } = useParams();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [showPdfOnly, setShowPdfOnly] = useState(false);
  const [count, setCount] = useState(0);
  const [accessChecked, setAccessChecked] = useState(false);
  const [accessBlocked, setAccessBlocked] = useState(false);
  const [pozoSeleccionado, setPozoSeleccionado] = useState(wellId || "todos");
  // Carpeta activa: null = grilla de carpetas, si no = key de FOLDERS
  const [carpetaActiva, setCarpetaActiva] = useState(null);
  const tour = useDocumentosTour({ enabled: !wellId });

  const inputFileRef = useRef(null);
  const navigate = useNavigate();

  const pdfCache = usePdfCache();
  const apiUrl = process.env.REACT_APP_API_URL;
  const isLimitReached = count >= 300;
  const canUpload = auth.role === "superAdmin" || auth.role === "testing";

  const demoDocs = useMemo(() => [
    { _id: "demo1", fileName: "parte-diario-001.pdf", createdAt: new Date().toISOString(), gcsUrl: "#", categoria: "partes_diarios", campos: { fecha: "2024-01-15", turno: "Mañana", proyecto: "Proyecto Demo", pozo: "Pozo-001" } },
    { _id: "demo2", fileName: "parte-diario-002.pdf", createdAt: new Date(Date.now() - 86400000).toISOString(), gcsUrl: "#", categoria: "partes_diarios", campos: { fecha: "2024-01-14", turno: "Noche", proyecto: "Proyecto Demo", pozo: "Pozo-002" } },
    { _id: "demo3", fileName: "parte-diario-003.pdf", createdAt: new Date(Date.now() - 172800000).toISOString(), gcsUrl: "#", categoria: "partes_diarios", campos: { fecha: "2024-01-13", turno: "Tarde", proyecto: "Proyecto Demo", pozo: "Pozo-003" } },
    { _id: "demo4", fileName: "seguro-personal-perez.pdf", createdAt: new Date(Date.now() - 200000000).toISOString(), gcsUrl: "#", categoria: "seguros_personales", campos: {} },
    { _id: "demo5", fileName: "seguro-vehiculo-camion01.pdf", createdAt: new Date(Date.now() - 260000000).toISOString(), gcsUrl: "#", categoria: "seguros_vehiculares", campos: {} },
  ], []);
  useEffect(() => {
    if (wellId) {
      const saveViewDocuments = async () => {
        const token = localStorage.getItem("auth_token") || sessionStorage.getItem("auth_token");
        try {
          await axios.post(
            `${apiUrl}/api/view-documents`,
            { pozo: wellId },
            { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} }
          );
        } catch (err) {
          console.error("❌ Error al registrar vista de documentos:", err);
        }
      };
      saveViewDocuments();
      setPozoSeleccionado(wellId);
    } else {
      setPozoSeleccionado("todos");
    }
  }, [wellId, apiUrl]);

  // Documentos que corresponden a la carpeta activa (por categoría)
  const docsDeLaCarpeta = useMemo(() => {
    if (!carpetaActiva) return [];
    return docs.filter((doc) => getCategoria(doc) === carpetaActiva);
  }, [docs, carpetaActiva]);

  // Dentro de "partes_diarios" además se puede filtrar por pozo
  const docsFiltrados = useMemo(() => {
    if (carpetaActiva !== "partes_diarios") return docsDeLaCarpeta;
    if (pozoSeleccionado === "todos") return docsDeLaCarpeta;
    return docsDeLaCarpeta.filter(doc => doc.campos?.pozo === pozoSeleccionado);
  }, [docsDeLaCarpeta, carpetaActiva, pozoSeleccionado]);

  const docsPorCarpeta = useMemo(() => {
    const map = {};
    FOLDERS.forEach((f) => { map[f.key] = 0; });
    docs.forEach((doc) => {
      const cat = getCategoria(doc);
      map[cat] = (map[cat] || 0) + 1;
    });
    return map;
  }, [docs]);

  const opcionesPozo = useMemo(() => {
    const pozosExistentes = [...new Set(
      docs.filter((d) => getCategoria(d) === "partes_diarios").map(doc => doc.campos?.pozo).filter(Boolean)
    )];
    if (wellId && !pozosExistentes.includes(wellId)) {
      pozosExistentes.unshift(wellId);
    }
    if (pozoSeleccionado !== "todos" && !pozosExistentes.includes(pozoSeleccionado)) {
      pozosExistentes.unshift(pozoSeleccionado);
    }
    return pozosExistentes;
  }, [docs, wellId, pozoSeleccionado]);

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const res = await axios.get(`${apiUrl}/api/route-access/documentos`, { withCredentials: true });
        const isBlocked = res.data.blocked && res.data.requireVerifiedEmail && !auth.isEmailVerified;
        setAccessBlocked(isBlocked);
      } catch { setAccessBlocked(false); }
      finally { setAccessChecked(true); }
    };
    refreshAuth();
    checkAccess();

  }, [apiUrl, auth.isEmailVerified]);

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      if (accessBlocked) { setTimeout(() => { setDocs(demoDocs); setLoading(false); }, 800); return; }
      const token = localStorage.getItem("auth_token") || sessionStorage.getItem("auth_token");
      const res = await axios.get(`${apiUrl}/api/ocr-results-db`, { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} });
      setDocs(res.data);
      setCount(res.data.length);
    } catch (err) { console.error(err); }
    finally { if (!accessBlocked) setLoading(false); }
  }, [apiUrl, accessBlocked, demoDocs]);

  const handleDownload = useCallback(async (url) => {
    if (accessBlocked) return;
    try {
      const fileName = url.split("/").pop();
      const token = localStorage.getItem("auth_token") || sessionStorage.getItem("auth_token");
      const res = await axios.get(
        `${apiUrl}/api/download-pdf?fileName=${encodeURIComponent(fileName)}`,
        { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      window.open(res.data.url, "_blank");
    } catch (err) { console.error(err); }
  }, [apiUrl, accessBlocked]);

  const handleDownloadAll = useCallback(async () => {
    if (accessBlocked || docsFiltrados.length === 0) return;
    setDownloadingZip(true);
    try {
      const token = localStorage.getItem("auth_token") || sessionStorage.getItem("auth_token");

      const pozoParam = carpetaActiva === "partes_diarios" && pozoSeleccionado !== "todos" ? pozoSeleccionado : undefined;
      const response = await axios.get(
        `${apiUrl}/api/download-all-pdfs`,
        {
          params: { categoria: carpetaActiva, ...(pozoParam ? { pozo: pozoParam } : {}) },
          withCredentials: true,
          responseType: "blob",
          timeout: 120000,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );

      const folderLabel = t(`document.folders.${carpetaActiva}.label`) || "Documentos";
      const zipName = pozoParam
        ? `${folderLabel} - ${pozoParam}.zip`
        : `${folderLabel} - Todos.zip`;

      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/zip" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", zipName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { console.error(err); throw err; }
    finally { setDownloadingZip(false); }
  }, [apiUrl, accessBlocked, docsFiltrados.length, pozoSeleccionado, carpetaActiva, t]);

  const handleFileChange = useCallback(async (e) => {
    if (accessBlocked) return;
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("categoria", carpetaActiva || "partes_diarios");
    if (carpetaActiva === "partes_diarios") {
      formData.append("pozo", pozoSeleccionado);
    }
    try {
      const token = localStorage.getItem("auth_token") || sessionStorage.getItem("auth_token");
      await axios.post(`${apiUrl}/api/upload`, formData, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });
      await fetchDocuments();
      e.target.value = null;
    } catch (err) { console.error(err); throw err; }
    finally { setUploading(false); }
  }, [apiUrl, fetchDocuments, accessBlocked, carpetaActiva, pozoSeleccionado]);

  const toggleExpand = useCallback((fileName, showPdf = false) => {
    if (accessBlocked) return;
    if (expanded === fileName && showPdfOnly === showPdf) { setExpanded(null); setShowPdfOnly(false); }
    else { setExpanded(fileName); setShowPdfOnly(showPdf); }
  }, [expanded, showPdfOnly, accessBlocked]);

  const handleOpenFolder = useCallback((key) => {
    if (accessBlocked) return;
    setCarpetaActiva(key);
  }, [accessBlocked]);

  const handleBackToFolders = useCallback(() => {
    // Si venimos con un wellId en la ruta, "volver" también saca el filtro de pozo.
    setCarpetaActiva(null);
  }, []);

  useEffect(() => { if (accessChecked) fetchDocuments(); }, [accessChecked, accessBlocked, fetchDocuments]);
  useEffect(() => () => pdfCache.clearCache(), [pdfCache]);

  // Handle folder selection from the upload dropdown
  const handleUploadFolderSelected = useCallback((folderKey) => {
    if (accessBlocked) return;
    if (isLimitReached && auth.role !== "superAdmin") return;
    setCarpetaActiva(folderKey);
    // Use a small timeout to ensure carpetaActiva state propagates before triggering input
    setTimeout(() => inputFileRef.current?.click(), 50);
  }, [accessBlocked, isLimitReached, auth.role]);

  if (!accessChecked) {
    return (
      <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
        <HeaderSkeleton />
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={3}>
          {[1, 2, 3].map((i) => <Grid item xs={12} sm={6} md={4} key={i}><DocumentSkeleton /></Grid>)}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ position: "relative", px: { xs: 2, md: 4 }, py: 3 }}>

      {loading ? <HeaderSkeleton /> : (
        <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
          <Box sx={{
            mb: carpetaActiva ? 0 : 4, display: "flex", justifyContent: "space-between",
            flexWrap: "wrap", gap: 2, alignItems: "center",
            filter: accessBlocked ? "blur(2px)" : "none",
            opacity: accessBlocked ? 0.7 : 1,
            transition: "filter 0.3s, opacity 0.3s",
          }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800, color: DESIGN.text, letterSpacing: -0.5, lineHeight: 1.2 }}>
                {carpetaActiva ? t(`document.folders.${carpetaActiva}.label`) : t("document.title")}
              </Typography>
              <Typography variant="body2" sx={{ color: DESIGN.textSoft, mt: 0.5 }}>
                {carpetaActiva ? t(`document.folders.${carpetaActiva}.description`) : t("document.subtitle")}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
              {
                 !carpetaActiva && auth.userName === "Lzalazar179" &&
              <Button
                onClick={tour.reopen}
                size="small"
                startIcon={<HelpOutline sx={{ fontSize: 16 }} />}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.78rem",
                  color: DESIGN.blue,
                  bgcolor: DESIGN.accentSoft,
                  border: `1px solid rgba(41,121,255,0.18)`,
                  borderRadius: DESIGN.radiusSm,
                  px: 1.75,
                  py: 0.6,
                  height: 36,
                  "&:hover": {
                    bgcolor: "rgba(41,121,255,0.15)",
                    borderColor: DESIGN.blue,
                  },
                  transition: "all 0.2s",
                }}
              >
                {t("document.tutorial", "Ver tutorial")}
              </Button>

              }

              {canUpload && (
                <>
                  <UploadFolderButton
                    uploading={uploading}
                    disabled={(isLimitReached && auth.role !== "superAdmin") || accessBlocked}
                    onFolderSelected={handleUploadFolderSelected}
                    t={t}
                    carpetaActiva={carpetaActiva}
                  />
                  <input type="file" accept="application/pdf" ref={inputFileRef} onChange={handleFileChange} style={{ display: "none" }} />
                </>
              )}
              {carpetaActiva && (
                <DownloadSplitButton
                  onDownloadZip={handleDownloadAll}
                  loading={downloadingZip}
                  disabled={docsFiltrados.length === 0 || accessBlocked}
                  count={docsFiltrados.length}
                  t={t}
                />
              )}
            </Box>
          </Box>
        </motion.div>
      )}

      {/* --------------------------------------------------------------- */}
      {/* Vista de carpetas                                                */}
      {/* --------------------------------------------------------------- */}
      {!carpetaActiva && (
        <Box
          data-tour="folder-grid"
          sx={{
            filter: accessBlocked ? "blur(3px)" : "none",
            opacity: accessBlocked ? 0.6 : 1,
            transition: "filter 0.3s, opacity 0.3s",
          }}
        >
          {loading ? (
            <Grid container spacing={2.5}>
              {[1, 2, 3, 4, 5].map((i) => <Grid item xs={12} sm={6} md={4} key={i}><FolderSkeleton /></Grid>)}
            </Grid>
          ) : (
            <FolderGrid docsPorCarpeta={docsPorCarpeta} onOpenFolder={handleOpenFolder} t={t} />
          )}
        </Box>
      )}

      {/* --------------------------------------------------------------- */}
      {/* Vista dentro de una carpeta                                      */}
      {/* --------------------------------------------------------------- */}
      {carpetaActiva && (
        <>
          <FolderBreadcrumb folderLabel={t(`document.folders.${carpetaActiva}.label`)} onBack={handleBackToFolders} t={t} />

          {carpetaActiva === "partes_diarios" && (
            <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
              <Box sx={{
                display: { xs: "none", sm: "flex" }, alignItems: "center", gap: 0.75,
                px: 1.5, py: 0.5, bgcolor: DESIGN.blue, borderRadius: "8px", flexShrink: 0,
              }}>
                <Box sx={{
                  width: 8, height: 8, borderRadius: "50%", bgcolor: "#00E5FF",
                  boxShadow: "0 0 6px rgba(0,229,255,0.9)",
                  animation: "pulse 2s ease-in-out infinite",
                  "@keyframes pulse": {
                    "0%, 100%": { opacity: 1, transform: "scale(1)" },
                    "50%": { opacity: 0.5, transform: "scale(1.4)" },
                  },
                }} />
                <Typography sx={{ fontSize: "0.7rem", fontWeight: 800, color: "#fff", letterSpacing: 1, textTransform: "uppercase" }}>
                  {t("document.pozo")}
                </Typography>
              </Box>
              <FormControl sx={{
                minWidth: { xs: "100%", sm: 200 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px", fontSize: "1rem", fontWeight: 700,
                  height: 52, bgcolor: "#fff", border: `2px solid ${DESIGN.blue}`,
                  "& fieldset": { border: "none" },
                  "&:hover": { bgcolor: "#f0f4ff" },
                  "&.Mui-focused": { boxShadow: `0 0 0 3px rgba(21,101,192,0.18)` },
                },
                "& .MuiInputLabel-root": { fontSize: "0.875rem", fontWeight: 600, color: DESIGN.blue },
                "& .MuiInputLabel-root.Mui-focused": { color: DESIGN.blue },
                "& .MuiSelect-select": { color: DESIGN.text },
              }}>
                <Select
                  value={pozoSeleccionado}
                  onChange={(e) => setPozoSeleccionado(e.target.value)}
                  displayEmpty
                >
                  {opcionesPozo.map((p) => (
                    <MenuItem key={p} value={p} sx={{ fontSize: "1.05rem", fontWeight: 600 }}>
                      {p}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {auth.role === "superAdmin" && !accessBlocked && (
            <Box sx={{
              mb: 3, display: "flex", alignItems: "center", gap: 2,
              p: "10px 16px", borderRadius: DESIGN.radiusSm,
              bgcolor: DESIGN.surfaceAlt, border: `1px solid ${DESIGN.border}`,
              width: "fit-content",
            }}>
              <Typography sx={{ fontSize: "0.78rem", color: DESIGN.textSoft, fontWeight: 600 }}>
                {t("document.pdfs_uploaded")}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography sx={{ fontWeight: 800, fontSize: "1rem", color: isLimitReached ? "#ef4444" : "#22c55e" }}>
                  {count}
                </Typography>
                <Typography sx={{ color: DESIGN.textSoft, fontSize: "0.85rem" }}>/</Typography>
                <Typography sx={{ fontWeight: 600, fontSize: "0.85rem", color: DESIGN.textSoft }}>300</Typography>
              </Box>
              <Box sx={{ width: 80, height: 4, bgcolor: DESIGN.border, borderRadius: 2, overflow: "hidden" }}>
                <Box sx={{
                  height: "100%", borderRadius: 2,
                  width: `${Math.min(100, (count / 300) * 100)}%`,
                  bgcolor: isLimitReached ? "#ef4444" : DESIGN.blue,
                  transition: "width 0.4s ease",
                }} />
              </Box>
            </Box>
          )}

          <Divider sx={{
            mb: 3,
            filter: accessBlocked ? "blur(2px)" : "none",
            opacity: accessBlocked ? 0.5 : 1,
            transition: "all 0.3s",
          }} />

          {loading ? (
            <Grid container spacing={2.5}>
              {[1, 2, 3, 4, 5, 6].map((i) => <Grid item xs={12} sm={6} md={4} key={i}><DocumentSkeleton /></Grid>)}
            </Grid>
          ) : docsFiltrados.length === 0 ? (
            <Box sx={{
              mt: 8, textAlign: "center",
              filter: accessBlocked ? "blur(2px)" : "none",
            }}>
              <Box sx={{
                width: 64, height: 64, borderRadius: "50%",
                bgcolor: DESIGN.accentSoft, border: `1px solid ${DESIGN.border}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                mx: "auto", mb: 2,
              }}>
                <Description sx={{ fontSize: 28, color: DESIGN.blue, opacity: 0.6 }} />
              </Box>
              <Typography variant="body1" sx={{ color: DESIGN.textSoft }}>{t("document.noDocuments")}</Typography>
            </Box>
          ) : (
            <Box sx={{
              filter: accessBlocked ? "blur(3px)" : "none",
              opacity: accessBlocked ? 0.6 : 1,
              transition: "filter 0.3s, opacity 0.3s",
            }}>
              {/* <Grid container sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 2,
              }}> */}
              <Grid container spacing={1.5}>
                {docsFiltrados.map((doc, i) => (
                  <Grid item xs={12} sm={6} md={4} key={doc._id}>

                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: i * 0.04 }}
                    >
                      <DocumentCard
                        doc={doc}
                        onDownload={handleDownload}
                        onToggle={(fileName) => toggleExpand(fileName, false)}
                        isOpen={expanded === doc.fileName}
                        onSaved={fetchDocuments}
                        isSuperAdmin={auth.role === "superAdmin"}
                        isTestingRole={auth.role === "testing"}
                        showPdfOnly={showPdfOnly && expanded === doc.fileName}
                        pdfCache={pdfCache}
                        isBlurred={accessBlocked}
                        wellId={wellId}
                      />
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </>
      )}

      {accessBlocked && (
        <NonBlockingOverlay>
          <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 0.4 }}>
            <Paper elevation={0} sx={{
              textAlign: "center", p: 5,
              bgcolor: "white",
              borderRadius: DESIGN.radius,
              boxShadow: "0 24px 64px rgba(10,15,46,0.18)",
              border: `1px solid ${DESIGN.border}`,
              maxWidth: 440, mx: 2,
            }}>
              <Box sx={{
                width: 72, height: 72, borderRadius: "50%",
                background: `linear-gradient(135deg, ${DESIGN.accentSoft}, rgba(41,121,255,0.08))`,
                border: `2px solid rgba(41,121,255,0.2)`,
                display: "flex", alignItems: "center", justifyContent: "center",
                mx: "auto", mb: 3,
              }}>
                <MailOutline sx={{ fontSize: 32, color: DESIGN.blue }} />
              </Box>

              <Typography variant="h5" sx={{ fontWeight: 800, color: DESIGN.text, mb: 1, letterSpacing: -0.3 }}>
                {t("document.verify_email_title")}
              </Typography>
              <Typography variant="body2" sx={{ color: DESIGN.textSoft, mb: 3.5, lineHeight: 1.7 }}>
                {t("document.verify_email_needed")}
              </Typography>

              <Button
                variant="contained"
                onClick={() => navigate("/panel/profile")}
                startIcon={<MailOutline />}
                sx={{
                  height: 44, px: 3.5, borderRadius: DESIGN.radiusSm,
                  fontWeight: 700, fontSize: "0.875rem", textTransform: "none",
                  background: `linear-gradient(135deg, ${DESIGN.blue}, ${DESIGN.blueBright})`,
                  boxShadow: "none",
                  "&:hover": { background: `linear-gradient(135deg, #1251a3, #1f5fcc)`, boxShadow: "0 8px 24px rgba(21,101,192,0.3)" },
                  transition: "all 0.2s",
                }}
              >
                {t("document.verify_email")}
              </Button>
            </Paper>
          </motion.div>
        </NonBlockingOverlay>
      )}

      <DocumentosOnboardingTour
        open={tour.open}
        onDismiss={() => { tour.dismiss(); setCarpetaActiva(null); }}
        onComplete={() => { tour.complete(); setCarpetaActiva(null); }}
      />
    </Box>
  );
};

export default React.memo(Documentos);